/**
 * A HOC wrapping Material UIs table Component. Used to unify the styles across all of our buttons.
 */
import { get, omit } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'tss-react/mui';
import { Button } from '@mui/material';

const leftRightPadding = (small, remove) => {
  if (remove) return '0px';
  return small ? '24px' : '6px';
};

const getLabelWidth = (small, auto) => {
  if (auto) return 'auto';
  return small ? '100px' : '199px';
};
const getBackgroundColor = (theme, props) => {
  // TODO: fix palette
  // Temporary fix for 'default' color with contained which comes from MUI and is not in our palette
  if (props.small && props.variant === 'contained') {
    return '#e0e0e0';
  }

  if (props.small || props.variant === 'outlined') {
    return undefined;
  }

  return get(
    theme,
    `palette.${props.color}.main`,
    get(theme, 'palette.primary.main'),
  );
};
const styles = (theme, props) => ({
  root: {
    alignItems: 'center',
    backgroundColor: getBackgroundColor(theme, props),
    borderColor: get(
      theme,
      `palette.${props.color}.main`,
      get(theme, 'palette.primary.main'),
    ),
    borderRadius: props.small ? '4px' : '0px',
    border:
      props.variant || props.small
        ? `1px solid ${get(
            theme,
            `palette.${props.color}.main`,
            get(theme, 'palette.primary.main'),
          )}`
        : 'default',
    color:
      props.small || props.variant === 'outlined'
        ? get(
            theme,
            `palette.${props.color}.main`,
            get(theme, 'palette.primary.main'),
          )
        : get(theme, 'palette.white.main'),
    display: props.flex ? 'flex' : 'inline',
    fontSize: props.small ? '12px' : '14px',
    fontWeight: props.small ? 'normal' : 'bold',
    height: props.height || 'auto',
    justifyContent: 'space-between',
    minWidth: props.small ? '120px' : '210px',
    paddingTop: props.small ? '7px' : '18px',
    paddingBottom: props.small ? '7px' : '14px',
    paddingRight: leftRightPadding(props.small, props.icon),
    paddingLeft: leftRightPadding(props.small, props.icon),
    textTransform: props.small ? 'none' : 'uppercase',
    width: props.width || 'auto',
    '&:disabled': {
      color:
        props.small || props.variant === 'outlined'
          ? get(
              theme,
              `palette.${props.color}.light`,
              get(theme, 'palette.primary.light'),
            )
          : get(theme, 'palette.white.light'),
    },
    '& span:first-of-type': {
      height: props.small ? 'default' : 'auto',
    },
    '& g': {
      fill:
        props.small || props.variant === 'outlined'
          ? get(
              theme,
              `palette.${props.color}.main`,
              get(theme, 'palette.primary.main'),
            )
          : get(theme, 'palette.white.main'),
    },
    '& use': {
      fill:
        props.small || props.variant === 'outlined'
          ? get(
              theme,
              `palette.${props.color}.main`,
              get(theme, 'palette.primary.main'),
            )
          : get(theme, 'palette.white.main'),
    },
    '&:hover': {
      backgroundColor: get(
        theme,
        `palette.${props.color}.light`,
        get(theme, 'palette.primary.light'),
      ),
      '& g': {
        fill: get(theme, 'palette.white.main'),
      },
      '& use': {
        fill: get(theme, 'palette.white.main'),
      },
    },
  },
  label: {
    height: 'auto',
    width: getLabelWidth(props.small, !props.icon),
    // Matches old MUI span styling
    display: 'inherit',
    alignItems: 'inherit',
    justifyContent: 'inherit',
  },
});

const ReusableButton = withStyles((props) => {
  const { classes, children } = props;

  return (
    <Button
      className={classes.root}
      {...omit(
        props,
        'icon',
        'flex',
        'small',
        'color',
        'borderColor',
        'children',
      )}
    >
      <span className={classes.label}>{children}</span>
    </Button>
  );
}, styles);

ReusableButton.propTypes = {
  flex: PropTypes.bool,
  icon: PropTypes.bool,
  small: PropTypes.bool,
};

export default ReusableButton;
